import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { Col, Form, Row } from 'react-bootstrap'
import sanitizeHtml from 'sanitize-html'
import moment from 'moment-timezone'
import { useErrorService } from '@abroad/components'
import ReturnLink from './ReturnLink'
import GoToNextStepBtn from './GoToNextStepBtn'
import AttachAudioResource from './AttachAudioResource'
import CKEditor from '../editor'
import ShowCharsLimt from '../common/ShowCharsLimt'
import useToSetCharsLimit from '../common/hooks/useCountCharactersLimit'
import { SelectionField } from '../common'
import CONSTANTS from '../../utils/constants'
import API from '../../utils/API'

const GrowthSessionPractice = ({
  growthSessionData,
  sessionId,
  nextStepHandler,
  prevStepHandler,
}) => {
  const { clientId, sowId } = useParams()
  const location = useLocation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [psp, setPSP] = useState('')
  const [isEditorReady, setIsEditorReady] = useState(false)
  const [saveResourcePdf, setSaveResourcePdf] = useState({
    mediaModules: [],
    medias: [],
  })
  const {
    totalChars: totalCharsInPSP,
    updateCharsLength: updateCharsLengthForPSP,
  } = useToSetCharsLimit()
  const [audioResource, setAudioResource] = useState({})
  const [scheduledEvents, setScheduledEvents] = useState([])
  const [isLoadingScheduledEvents, setIsLoadingScheduledEvents] =
    useState(false)
  const [scheduledEventId, setScheduledEventId] = useState('')
  const Error = useErrorService()

  useEffect(() => {
    if (isEditorReady) {
      setPSP(growthSessionData?.postSession || '')
    }
  }, [growthSessionData, isEditorReady])

  useEffect(() => {
    const fetchScheduledEvents = async () => {
      if (!sowId) {
        setScheduledEvents([])
        return
      }
      setIsLoadingScheduledEvents(true)
      const fromScheduledBooking = !!location.state?.fromScheduledBooking
      const scheduledEventIdFromState =
        location.state?.scheduledEventId ||
        location.state?.scheduleEventId ||
        null
      const selectedEventFromState = location.state?.scheduledEvent

      const buildBookingListOption = () => {
        if (!scheduledEventIdFromState) return null
        let label = selectedEventFromState?.title || 'Selected calendar event'
        if (selectedEventFromState?.startDateTime) {
          const formattedDate = moment(
            selectedEventFromState.startDateTime,
          ).format('MM/DD/YYYY')
          label = `${formattedDate}-${selectedEventFromState.title || 'Event'}`
        }
        return {
          label,
          value: String(scheduledEventIdFromState),
        }
      }
      const bookingListOption = buildBookingListOption()

      try {
        // Keep sessionType=growth restriction for growth session logging
        const { data } = await API.AdminAPI.coachPlan.getScheduledEvents(
          sowId,
          {
            sessionType: 'growth',
          },
        )
        if (data && Array.isArray(data)) {
          const getScheduledEventOptionId = (event) =>
            event?._id ||
            event?.scheduleEventId ||
            event?.scheduledEventId ||
            event?.id ||
            null

          const formattedEvents = data
            .map((event) => {
              const eventId = getScheduledEventOptionId(event)
              if (!eventId) return null
              const formattedDate = moment(event.startTime).format('MM/DD/YYYY')
              return {
                label: `${formattedDate}-${event.title}`,
                value: String(eventId),
              }
            })
            .filter(Boolean)

          // From booking list: show only the selected scheduled event
          if (fromScheduledBooking && scheduledEventIdFromState) {
            const matchedRaw = data.find((event) => {
              const ids = [
                event?._id,
                event?.scheduleEventId,
                event?.scheduledEventId,
                event?.id,
              ]
                .filter(Boolean)
                .map(String)
              return ids.includes(String(scheduledEventIdFromState))
            })
            const matchedOptionId = getScheduledEventOptionId(matchedRaw)
            const onlySelectedEvent =
              (matchedOptionId
                ? {
                    label: `${moment(matchedRaw.startTime).format(
                      'MM/DD/YYYY',
                    )}-${matchedRaw.title}`,
                    value: String(matchedOptionId),
                  }
                : null) || bookingListOption

            setScheduledEvents(onlySelectedEvent ? [onlySelectedEvent] : [])
            if (onlySelectedEvent) {
              setScheduledEventId(String(onlySelectedEvent.value))
            }
          } else {
            setScheduledEvents(formattedEvents)
          }
        } else if (fromScheduledBooking && bookingListOption) {
          setScheduledEvents([bookingListOption])
          setScheduledEventId(bookingListOption.value)
        } else {
          setScheduledEvents([])
        }
        setIsLoadingScheduledEvents(false)
      } catch (e) {
        if (fromScheduledBooking && bookingListOption) {
          setScheduledEvents([bookingListOption])
          setScheduledEventId(bookingListOption.value)
        } else {
          setScheduledEvents([])
        }
        setIsLoadingScheduledEvents(false)
        // Don't show error for scheduled events as it's optional
      }
    }
    fetchScheduledEvents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sowId])

  const handleSubmit = async () => {
    setIsSubmitting(true)
    const params = {
      postSession: psp,
      mediaIds: audioResource?.mediaIds,
      moduleIds: audioResource?.moduleIds,
      keySkillIds: audioResource?.keySkillsIds,
      sessionId: sessionId,
    }
    if (scheduledEventId) {
      params.scheduledEventId = scheduledEventId
    }
    try {
      const { data } = await API.CoachAPI.coachingSession.updateGrowthSession(
        clientId,
        sowId,
        params,
      )
      if (data) {
        setIsSubmitting(false)
        nextStepHandler({ ...saveResourcePdf, postSession: psp ? psp : '' })
      }
    } catch (e) {
      setIsSubmitting(false)
      Error.showError(e)
    }
  }

  return (
    <section className='explore-offerings d-flex flex-column session-practice-wrapper pb-3'>
      <Row className='profile'>
        <Col
          lg={2}
          md={12}
          sm={12}
          className='d-lg-flex align-items-lg-center justify-content-lg-end pr-lg-1'>
          <Form.Label>Calendar Event</Form.Label>
        </Col>
        <Col lg={10} md={12} sm={12}>
          <SelectionField
            name='scheduledEventId'
            size='lg'
            options={scheduledEvents}
            getValueAsOption={true}
            optionplaceholder='Select one'
            className='mb-3'
            isUseFormikSelectionField={false}
            value={scheduledEventId}
            onChange={(e) => setScheduledEventId(e.target.value)}
            disabled={
              isSubmitting ||
              isLoadingScheduledEvents ||
              scheduledEvents.length === 0 ||
              !!location.state?.fromScheduledBooking
            }
          />
        </Col>
      </Row>
      <Row className='profile'>
        <Col
          lg={2}
          md={12}
          sm={12}
          className='d-lg-flex align-items-lg-center justify-content-lg-end pr-lg-1'>
          <Form.Label>Post Session Practice (optional)</Form.Label>
        </Col>
        <Col lg={10} md={12} sm={12}>
          <CKEditor
            data={psp}
            placeholder='Post Session practices are recommended, though not required for a
          growth plan. Feel free to leave notes here for your client to see, as
          well as recommend audio and written content.'
            onReady={(editor) => {
              setIsEditorReady(true)
            }}
            onChange={(event, editor) => {
              updateCharsLengthForPSP(
                sanitizeHtml(editor.getData(), {
                  allowedTags: [],
                  allowedAttributes: {},
                })?.length,
              )
              setPSP(editor.getData())
            }}
            disabled={isSubmitting}
          />
        </Col>
      </Row>
      <ShowCharsLimt
        limit={CONSTANTS.charsLimitInPSP}
        enteredChars={totalCharsInPSP}
      />
      <AttachAudioResource
        growthPlanData={growthSessionData}
        setAudioResource={setAudioResource}
        setSaveResourcePdf={setSaveResourcePdf}
        disabled={isSubmitting}
      />
      <div className='d-flex justify-content-between mt-3 mb-5'>
        <ReturnLink
          prevStepHandler={prevStepHandler}
          isDisabled={isSubmitting}
        />
        <GoToNextStepBtn
          nextStepHandler={handleSubmit}
          isDisabled={
            isSubmitting || totalCharsInPSP > CONSTANTS.charsLimitInPSP
          }
          isLoading={isSubmitting}
        />
      </div>
    </section>
  )
}

export default GrowthSessionPractice
